import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Oval } from "react-loader-spinner";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import Container from "../components/common/Container";

/**
 * OAuthLinkResult Page
 * 
 * This page is displayed inside the popup window after OAuth linking completes.
 * It communicates the result back to the parent window using postMessage and closes automatically.
 * 
 * Flow:
 * 1. Backend redirects here after OAuth linking with success/error params
 * 2. This page sends the result to the parent window via postMessage
 * 3. The popup closes automatically
 * 4. Parent window receives the message and updates UI
 */
const OAuthLinkResult = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const linkSuccess = searchParams.get("linkSuccess");
    const linkError = searchParams.get("linkError");

    // Determine the result
    let result = {
      type: "oauth_link_result",
      success: false,
      provider: null,
      error: null,
    };

    if (linkSuccess) {
      result.success = true;
      result.provider = linkSuccess;
      setStatus("success");
      setMessage(`${linkSuccess.charAt(0).toUpperCase() + linkSuccess.slice(1)} account linked successfully!`);
    } else if (linkError) {
      result.success = false;
      result.error = linkError;
      setStatus("error");
      
      // Format error message
      let errorMessage = "Failed to link account";
      switch (linkError) {
        case "already_linked_to_another_account":
          errorMessage = "This account is already linked to another user";
          break;
        case "invalid_state":
          errorMessage = "Session expired. Please try again.";
          break;
        case "invalid_provider":
          errorMessage = "Invalid provider";
          break;
        case "token_exchange_failed":
          errorMessage = "Failed to authenticate with provider";
          break;
        case "registration_required":
          errorMessage = "Please complete your profile registration first, then link your accounts.";
          break;
        default:
          errorMessage = `Error: ${linkError}`;
      }
      result.errorMessage = errorMessage;
      setMessage(errorMessage);
    } else {
      // No params - could be a manual navigation
      setStatus("error");
      setMessage("No linking result received");
      result.error = "no_result";
      result.errorMessage = "No linking result received";
    }

    // Send result to parent window
    if (window.opener && !window.opener.closed) {
      try {
        // Try to send message - the parent window should be on the same origin
        window.opener.postMessage(result, "*"); // Use "*" as we're in a popup that may have lost origin
        
        // Auto-close after a brief delay so user can see the status
        setTimeout(() => {
          window.close();
        }, 1500);
      } catch (err) {
        console.error("Failed to send message to parent window:", err);
        // Still try to close
        setTimeout(() => {
          window.close();
        }, 2000);
      }
    } else {
      // No parent window or parent is closed
      // This can happen if:
      // 1. User navigated here directly (not in a popup)
      // 2. Parent window was closed
      // 3. Browser security blocked window.opener
      console.log("No window.opener available, will close after delay");
      
      // Still try to close the window (might work if it's a popup)
      setTimeout(() => {
        window.close();
      }, 2500);
    }
  }, [searchParams, message]);

  return (
    <Container className="min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md mx-auto px-4">
        {status === "processing" && (
          <>
            <Oval
              height={64}
              width={64}
              color="#792CF3"
              secondaryColor="#e5e7eb"
              strokeWidth={4}
              wrapperClass="justify-center mb-6"
            />
            <h2 className="text-2xl font-bold text-secondary mb-2">
              Processing...
            </h2>
            <p className="text-gray-500">
              Please wait while we complete the verification.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="flex justify-center mb-6">
              <FaCheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-secondary mb-2">
              Account Linked!
            </h2>
            <p className="text-gray-500 mb-4">
              {message}
            </p>
            <p className="text-sm text-gray-400">
              This window will close automatically...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="flex justify-center mb-6">
              <FaTimesCircle className="w-16 h-16 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-secondary mb-2">
              Linking Failed
            </h2>
            <p className="text-gray-500 mb-4">
              {message}
            </p>
            {window.opener ? (
              <p className="text-sm text-gray-400">
                This window will close automatically...
              </p>
            ) : (
              <button
                onClick={() => window.close()}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
              >
                Close Window
              </button>
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default OAuthLinkResult;
